import { setReplaceText } from '@alliander-fe/validation';

import { FilteredCongestionCheckerParticulierProps } from 'components/CongestionCheckerParticulier';
import { MappedCongestionDataProps } from '../types/mappedResponse';

export type MappedCongestionProps = {
  level?: string;
  disclaimer?: boolean;
  replaceText?: { [key: string]: string };
} & FilteredCongestionCheckerParticulierProps;

/**
 * @description This function is used to map the congestion data to the correct format.
 *
 * @param level The level of the congestion.
 * @param disclaimer The disclaimer text.
 * @param replaceText The replace text.
 * @param congestionData The congestion data.
 *
 * @returns The mapped congestion data.
 */
export const mappedResponseV2 = ({
  level,
  disclaimer,
  replaceText,
  ...congestionData
}: MappedCongestionProps): MappedCongestionDataProps => {
  const replaceTextParameters = replaceText ? replaceText : { '{}': '' };
  const disclaimerText = disclaimer
    ? congestionData.disclaimerTextCongestionNearbyConnection
    : undefined;

  switch (level) {
    case 'NoScarcity':
      return {
        feedbackTitle: setReplaceText(
          congestionData.feedbackNoScarcityTitle,
          replaceTextParameters
        ),
        feedbackText: setReplaceText(congestionData.feedbackNoScarcityText, replaceTextParameters),
        feedbackPrimaryButton: congestionData.feedbackNoScarcityPrimaryButton,
        feedbackSecondaryButton: congestionData.feedbackNoScarcitySecondaryButton,

        disclaimer: disclaimerText,

        additionalInfoBlockTitle: setReplaceText(
          congestionData.additionalInfoBlockNoScarcityTitle,
          replaceTextParameters
        ),
        additionalInfoBlockText: setReplaceText(
          congestionData.additionalInfoBlockNoScarcityText,
          replaceTextParameters
        ),
        additionalInfoBlockPrimaryButton: congestionData.additionalInfoBlockNoScarcityPrimaryButton,
        additionalInfoBlockSecondaryButton:
          congestionData.additionalInfoBlockNoScarcitySecondaryButton,

        conclusionUpperText: setReplaceText(
          congestionData.conclusionNoScarcityUpperText,
          replaceTextParameters
        ),
        conclusionFirstPrimaryButton: congestionData.conclusionNoScarcityFirstPrimaryButton,
        conclusionFirstSecondaryButton: congestionData.conclusionNoScarcityFirstSecondaryButton,
      };
    case 'AllConnectionsPossible':
      return {
        feedbackTitle: setReplaceText(
          congestionData.feedbackAllConnectionsPossibleTitle,
          replaceTextParameters
        ),
        feedbackText: congestionData.feedbackAllConnectionsPossibleText,
        feedbackPrimaryButton: congestionData.feedbackAllConnectionsPossiblePrimaryButton,
        feedbackSecondaryButton: congestionData.feedbackAllConnectionsPossibleSecondaryButton,

        disclaimer: disclaimerText,

        additionalInfoBlockTitle: setReplaceText(
          congestionData.additionalInfoBlockAllConnectionsPossibleTitle,
          replaceTextParameters
        ),
        additionalInfoBlockText: setReplaceText(
          congestionData.additionalInfoBlockAllConnectionsPossibleText,
          replaceTextParameters
        ),
        additionalInfoBlockPrimaryButton:
          congestionData.additionalInfoBlockAllConnectionsPossiblePrimaryButton,
        additionalInfoBlockSecondaryButton:
          congestionData.additionalInfoBlockAllConnectionsPossibleSecondaryButton,

        conclusionUpperText: setReplaceText(
          congestionData.conclusionAllConnectionsPossibleUpperText,
          replaceTextParameters
        ),
        conclusionFirstPrimaryButton:
          congestionData.conclusionAllConnectionsPossibleFirstPrimaryButton,
        conclusionFirstSecondaryButton:
          congestionData.conclusionAllConnectionsPossibleFirstSecondaryButton,
        conclusionLowerText: setReplaceText(
          congestionData.conclusionAllConnectionsPossibleLowerText,
          replaceTextParameters
        ),
        conclusionSecondPrimaryButton:
          congestionData.conclusionAllConnectionsPossibleSecondPrimaryButton,
        conclusionSecondSecondaryButton:
          congestionData.conclusionAllConnectionsPossibleSecondSecondaryButton,
      };
    case 'SmallConnectionsPossible':
      return {
        feedbackTitle: setReplaceText(
          congestionData.feedbackSmallConnectionsPossibleTitle,
          replaceTextParameters
        ),
        feedbackText: congestionData.feedbackSmallConnectionsPossibleText,
        feedbackPrimaryButton: congestionData.feedbackSmallConnectionsPossiblePrimaryButton,
        feedbackSecondaryButton: congestionData.feedbackSmallConnectionsPossibleSecondaryButton,

        disclaimer: disclaimerText,

        additionalInfoBlockTitle: setReplaceText(
          congestionData.additionalInfoBlockSmallConnectionsPossibleTitle,
          replaceTextParameters
        ),
        additionalInfoBlockText: setReplaceText(
          congestionData.additionalInfoBlockSmallConnectionsPossibleText,
          replaceTextParameters
        ),
        additionalInfoBlockPrimaryButton:
          congestionData.additionalInfoBlockSmallConnectionsPossiblePrimaryButton,
        additionalInfoBlockSecondaryButton:
          congestionData.additionalInfoBlockSmallConnectionsPossibleSecondaryButton,

        conclusionUpperText: setReplaceText(
          congestionData.conclusionSmallConnectionsPossibleUpperText,
          replaceTextParameters
        ),
        conclusionFirstPrimaryButton:
          congestionData.conclusionSmallConnectionsPossibleFirstPrimaryButton,
        conclusionFirstSecondaryButton:
          congestionData.conclusionSmallConnectionsPossibleFirstSecondaryButton,
        conclusionLowerText: setReplaceText(
          congestionData.conclusionSmallConnectionsPossibleLowerText,
          replaceTextParameters
        ),
        conclusionSecondPrimaryButton:
          congestionData.conclusionSmallConnectionsPossibleSecondPrimaryButton,
        conclusionSecondSecondaryButton:
          congestionData.conclusionSmallConnectionsPossibleSecondSecondaryButton,
      };
    case 'GridIsFull3Years':
      return {
        feedbackTitle: setReplaceText(
          congestionData.feedbackGridIsFull3YearsTitle,
          replaceTextParameters
        ),
        feedbackText: congestionData.feedbackGridIsFull3YearsText,
        feedbackPrimaryButton: congestionData.feedbackGridIsFull3YearsPrimaryButton,
        feedbackSecondaryButton: congestionData.feedbackGridIsFull3YearsSecondaryButton,

        disclaimer: disclaimerText,

        additionalInfoBlockTitle: setReplaceText(
          congestionData.additionalInfoBlockGridIsFull3YearsTitle,
          replaceTextParameters
        ),
        additionalInfoBlockText: setReplaceText(
          congestionData.additionalInfoBlockGridIsFull3YearsText,
          replaceTextParameters
        ),
        additionalInfoBlockPrimaryButton:
          congestionData.additionalInfoBlockGridIsFull3YearsPrimaryButton,
        additionalInfoBlockSecondaryButton:
          congestionData.additionalInfoBlockGridIsFull3YearsSecondaryButton,

        conclusionUpperText: setReplaceText(
          congestionData.conclusionGridIsFull3YearsUpperText,
          replaceTextParameters
        ),
        conclusionFirstPrimaryButton: congestionData.conclusionGridIsFull3YearsFirstPrimaryButton,
        conclusionFirstSecondaryButton:
          congestionData.conclusionGridIsFull3YearsFirstSecondaryButton,
        conclusionLowerText: setReplaceText(
          congestionData.conclusionGridIsFull3YearsLowerText,
          replaceTextParameters
        ),
        conclusionSecondPrimaryButton: congestionData.conclusionGridIsFull3YearsSecondPrimaryButton,
        conclusionSecondSecondaryButton:
          congestionData.conclusionGridIsFull3YearsSecondSecondaryButton,
      };
    case 'GridIsFullXYears':
      return {
        feedbackTitle: setReplaceText(
          congestionData.feedbackGridIsFullXYearsTitle,
          replaceTextParameters
        ),
        feedbackText: congestionData.feedbackGridIsFullXYearsText,
        feedbackPrimaryButton: congestionData.feedbackGridIsFullXYearsPrimaryButton,
        feedbackSecondaryButton: congestionData.feedbackGridIsFullXYearsSecondaryButton,

        disclaimer: disclaimerText,

        additionalInfoBlockTitle: setReplaceText(
          congestionData.additionalInfoBlockGridIsFullXYearsTitle,
          replaceTextParameters
        ),
        additionalInfoBlockText: setReplaceText(
          congestionData.additionalInfoBlockGridIsFullXYearsText,
          replaceTextParameters
        ),
        additionalInfoBlockPrimaryButton:
          congestionData.additionalInfoBlockGridIsFullXYearsPrimaryButton,
        additionalInfoBlockSecondaryButton:
          congestionData.additionalInfoBlockGridIsFullXYearsSecondaryButton,

        conclusionUpperText: setReplaceText(
          congestionData.conclusionGridIsFullXYearsUpperText,
          replaceTextParameters
        ),
        conclusionFirstPrimaryButton: congestionData.conclusionGridIsFullXYearsFirstPrimaryButton,
        conclusionFirstSecondaryButton:
          congestionData.conclusionGridIsFullXYearsFirstSecondaryButton,
        conclusionLowerText: setReplaceText(
          congestionData.conclusionGridIsFullXYearsLowerText,
          replaceTextParameters
        ),
        conclusionSecondPrimaryButton: congestionData.conclusionGridIsFullXYearsSecondPrimaryButton,
        conclusionSecondSecondaryButton:
          congestionData.conclusionGridIsFullXYearsSecondSecondaryButton,
      };
    case 'NotFound':
      return {
        feedbackTitle: congestionData.errorTitle
          ? setReplaceText(congestionData.errorTitle, replaceTextParameters)
          : 'Liander niet uw netbeheerder',
        feedbackText: congestionData.errorMessage
          ? setReplaceText(congestionData.errorMessage, replaceTextParameters)
          : 'We zien aan uw postcode dat Liander niet uw netbeheerder is. Controleer uw gegevens of zoek uw netbeheerder op via <a href="https://www.mijnaansluiting.nl/netbeheerders" target="_blank">mijnaansluiting.nl</a>',
      };
    default:
      return {
        feedbackTitle: congestionData.errorTitle || 'Er is iets fout gegaan',
        feedbackText:
          congestionData.errorMessage || 'Controleer uw gegevens en probeer het opnieuw.',
      };
  }
};
